import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Row, Col, Spinner, Badge, Container, Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import { FaArrowLeft, FaEdit, FaDownload, FaEye, FaFilePdf, FaFileImage, FaBuilding, FaPhone, FaEnvelope, FaUniversity, FaShieldAlt, FaMapMarkerAlt } from "react-icons/fa";

const CompanyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [companyData, setCompanyData] = useState(null);
  const [fetchedImages, setFetchedImages] = useState([]);
  const [showModel, setShowModel] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}company/findById/${1}`, {
          headers: authHeader(),
        });

        const Data = response.data;
        setCompanyData(Data);

        if (Data.companyImages && Data.companyImages.length > 0) {
          const imagePromises = Data.companyImages.map(async (i) => {
            try {
              const response = await axios.get(
                BASE_URL + `companyImages/findById/${i.id}`,
                {
                  headers: authHeader(),
                  responseType: "blob",
                }
              );
              const contentType = response.headers["content-type"];
              const blob = new Blob([response.data], { type: contentType });
              const fileUrl = URL.createObjectURL(blob);
              return {
                src: fileUrl,
                name: i.filename,
                type: i.contenttype,
                id: i.id
              };
            } catch (error) {
              console.log("error in image fetch", error);
              return null;
            }
          });

          const imageUrls = await Promise.all(imagePromises);
          setFetchedImages(imageUrls.filter(img => img !== null));
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch company details. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [id]);

  const handleCurrentFileView = (doc) => {
    setCurrentDoc(doc);
    setShowModel(true);
  };

  const handleCloseFileView = () => {
    setCurrentDoc(null);
    setShowModel(false);
  };

  const handleDownload = () => {
    if (currentDoc) {
      const link = document.createElement("a");
      link.href = currentDoc.src;
      link.download = currentDoc.name;
      link.click();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  const getFileIcon = (fileType) => {
    if (fileType === "application/pdf") {
      return <FaFilePdf className="text-danger" size={24} />;
    }
    return <FaFileImage className="text-primary" size={24} />;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', background: '#f8f9fa' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading company details...</p>
        </div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="alert alert-danger text-center mt-4">Company not found</div>
      </div>
    );
  }

  const isPolicyExpired = companyData.policyvalidtill && new Date(companyData.policyvalidtill) < new Date();

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      <Container fluid className="p-4">
        
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-3 p-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)' }}>
              <FaBuilding size={28} color="white" />
            </div>
            <div>
              <h4 className="fw-bold text-dark mb-1">Company Profile</h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>View and manage company information</p>
            </div>
          </div>
          <div>
            <Link to="/company/edit" className="btn btn-dark btn-sm shadow-sm">
              <FaEdit className="me-2" />
              Edit Company
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="g-3 mb-4">
          <Col md={4}>
            <div className="rounded-3 p-3 shadow-sm bg-white border-start border-4 border-dark">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <small className="text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Documents</small>
                  <h3 className="fw-bold mb-0 text-dark">{fetchedImages.length}</h3>
                </div>
                <div className="rounded-3 p-2" style={{ backgroundColor: '#f1f5f9' }}>
                  <FaFilePdf size={20} className="text-danger" />
                </div>
              </div>
              <small className="text-muted mt-2 d-block">Total uploaded documents</small>
            </div>
          </Col>
          <Col md={4}>
            <div className="rounded-3 p-3 shadow-sm bg-white border-start border-4 border-primary">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <small className="text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Addresses</small>
                  <h3 className="fw-bold mb-0 text-dark">{companyData.companyMultipleAddresses?.length || 0}</h3>
                </div>
                <div className="rounded-3 p-2" style={{ backgroundColor: '#eff6ff' }}>
                  <FaMapMarkerAlt size={20} className="text-primary" />
                </div>
              </div>
              <small className="text-muted mt-2 d-block">Registered company addresses</small>
            </div>
          </Col>
          <Col md={4}>
            <div className="rounded-3 p-3 shadow-sm bg-white border-start border-4 border-success">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <small className="text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Bank Account</small>
                  <h3 className="fw-bold mb-0 text-dark">1</h3>
                </div>
                <div className="rounded-3 p-2" style={{ backgroundColor: '#f0fdf4' }}>
                  <FaUniversity size={20} className="text-success" />
                </div>
              </div>
              <small className="text-muted mt-2 d-block">Active bank account</small>
            </div>
          </Col>
        </Row>

        {/* Company Summary Banner */}
        <div className="rounded-3 p-4 mb-4 shadow-sm" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white' }}>
          <Row className="align-items-center">
            <Col md={8}>
              <h5 className="fw-bold mb-1">{companyData.companyname || 'Company Name'}</h5>
              <div className="d-flex flex-wrap gap-4 mt-2" style={{ fontSize: '0.85rem' }}>
                <span className="d-flex align-items-center gap-1">
                  <FaEnvelope size={12} className="opacity-75" />
                  {companyData.email || 'N/A'}
                </span>
                <span className="d-flex align-items-center gap-1">
                  <FaPhone size={12} className="opacity-75" />
                  {companyData.workcontactno || companyData.officecontactno || 'N/A'}
                </span>
                <span className="d-flex align-items-center gap-1">
                  <FaShieldAlt size={12} className="opacity-75" />
                  GST: {companyData.gstno || 'N/A'}
                </span>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Badge bg={isPolicyExpired ? 'danger' : 'success'} className="px-3 py-2 rounded-pill">
                {isPolicyExpired ? 'Policy Expired' : 'Active'}
              </Badge>
            </Col>
          </Row>
        </div>

        {/* Basic & Contact Information */}
        <Row className="g-3 mb-4">
          <Col lg={6}>
            <div className="rounded-3 shadow-sm bg-white h-100">
              <div className="p-3 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                  <FaBuilding size={16} className="text-dark" />
                  Basic Information
                </h6>
              </div>
              <div className="p-3">
                <Row className="g-3">
                  <Col md={6}>
                    <small className="text-muted d-block">Company Name</small>
                    <span className="fw-bold">{companyData.companyname || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Contact Person</small>
                    <span className="fw-bold">{companyData.contactperson || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Email</small>
                    <span className="fw-bold">{companyData.email || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Work Contact</small>
                    <span className="fw-bold">{companyData.workcontactno || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Office Contact</small>
                    <span className="fw-bold">{companyData.officecontactno || '-'}</span>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>

          {/* Legal & Registration */}
          <Col lg={6}>
            <div className="rounded-3 shadow-sm bg-white h-100">
              <div className="p-3 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                  <FaShieldAlt size={16} className="text-primary" />
                  Legal & Registration
                </h6>
              </div>
              <div className="p-3">
                <Row className="g-3">
                  <Col md={6}>
                    <small className="text-muted d-block">PAN Number</small>
                    <Badge bg="light" text="dark" className="fw-normal px-2 py-1">{companyData.panno || '-'}</Badge>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">GST Number</small>
                    <Badge bg="light" text="dark" className="fw-normal px-2 py-1">{companyData.gstno || '-'}</Badge>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">CIN Number</small>
                    <span className="fw-bold">{companyData.cinno || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">LUT Number</small>
                    <span className="fw-bold">{companyData.lutno || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Udyam Registration</small>
                    <span className="fw-bold">{companyData.udyamregistrationno || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">IEC Number</small>
                    <span className="fw-bold">{companyData.iecno || '-'}</span>
                  </Col>
                  <Col md={12}>
                    <small className="text-muted d-block">Incorporation Date</small>
                    <span className="fw-bold">{formatDate(companyData.incorporationdate)}</span>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {/* Bank & Insurance Details */}
        <Row className="g-3 mb-4">
          <Col lg={6}>
            <div className="rounded-3 shadow-sm bg-white h-100">
              <div className="p-3 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                  <FaUniversity size={16} className="text-success" />
                  Bank Details
                </h6>
              </div>
              <div className="p-3">
                <Row className="g-3">
                  <Col md={6}>
                    <small className="text-muted d-block">Bank Name</small>
                    <span className="fw-bold">{companyData.bankname || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Account Number</small>
                    <span className="fw-bold">{companyData.accountnumber || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Branch Name</small>
                    <span className="fw-bold">{companyData.branchname || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">IFSC Code</small>
                    <Badge bg="warning" text="dark" className="fw-normal px-2 py-1">{companyData.ifsccode || '-'}</Badge>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="rounded-3 shadow-sm bg-white h-100">
              <div className="p-3 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                  <FaShieldAlt size={16} className="text-info" />
                  Insurance Details
                </h6>
              </div>
              <div className="p-3">
                <Row className="g-3">
                  <Col md={6}>
                    <small className="text-muted d-block">Policy Number</small>
                    <span className="fw-bold">{companyData.policynumber || '-'}</span>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted d-block">Valid Till</small>
                    <span className="fw-bold">{formatDate(companyData.policyvalidtill)}</span>
                  </Col>
                  <Col md={12}>
                    {isPolicyExpired ? (
                      <Badge bg="danger" className="px-3 py-2 rounded-pill mt-2">
                        <FaShieldAlt className="me-1" size={10} />
                        Policy Expired
                      </Badge>
                    ) : companyData.policynumber ? (
                      <Badge bg="success" className="px-3 py-2 rounded-pill mt-2">
                        <FaShieldAlt className="me-1" size={10} />
                        Policy Active
                      </Badge>
                    ) : null}
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {/* Company Addresses */}
        <div className="rounded-3 shadow-sm bg-white mb-4">
          <div className="p-3 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
            <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
              <FaMapMarkerAlt size={16} className="text-danger" />
              Company Addresses
            </h6>
          </div>
          <div className="p-3">
            {companyData.companyMultipleAddresses && companyData.companyMultipleAddresses.length > 0 ? (
              <Row className="g-3">
                {companyData.companyMultipleAddresses.map((addr, index) => (
                  <Col lg={6} key={index}>
                    <div className="border rounded-3 p-3 h-100" style={{ borderLeft: '4px solid #0f172a !important' }}>
                      <Badge bg="dark" className="mb-3 px-3 py-2 rounded-pill">
                        Address {index + 1}
                      </Badge>
                      <p className="fw-bold mb-3" style={{ fontSize: '0.9rem' }}>{addr.address || 'N/A'}</p>
                      <Row className="g-2 small">
                        <Col xs={6}>
                          <span className="text-muted">City:</span>
                          <span className="fw-semibold ms-1">{addr.city?.cityname || 'N/A'}</span>
                        </Col>
                        <Col xs={6}>
                          <span className="text-muted">State:</span>
                          <span className="fw-semibold ms-1">{addr.state?.statename || 'N/A'}</span>
                        </Col>
                        <Col xs={6}>
                          <span className="text-muted">Country:</span>
                          <span className="fw-semibold ms-1">{addr.country?.countryname || 'N/A'}</span>
                        </Col>
                        <Col xs={6}>
                          <span className="text-muted">Zip Code:</span>
                          <span className="fw-semibold ms-1">{addr.zipcode || 'N/A'}</span>
                        </Col>
                        <Col xs={12}>
                          <Badge bg="secondary" className="mt-2 px-2 py-1">
                            State Code: {addr.statecode || 'N/A'}
                          </Badge>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5">
                <FaMapMarkerAlt size={48} className="text-muted mb-3 opacity-25" />
                <p className="text-muted">No addresses found</p>
              </div>
            )}
          </div>
        </div>

        {/* Company Documents */}
        {fetchedImages.length > 0 && (
          <div className="rounded-3 shadow-sm bg-white mb-4">
            <div className="p-3 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
              <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                <FaFilePdf size={16} className="text-danger" />
                Company Documents
              </h6>
            </div>
            <div className="p-3">
              <Row className="g-3">
                {fetchedImages.map((doc, index) => (
                  <Col md={4} xl={3} key={index}>
                    <div 
                      className="border rounded-3 p-3 text-center shadow-sm h-100"
                      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                      onClick={() => handleCurrentFileView(doc)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                      }}
                    >
                      <div className="mb-3">
                        {getFileIcon(doc.type)}
                      </div>
                      <div className="fw-semibold small text-truncate mb-2">
                        {doc.name}
                      </div>
                      <div className="d-flex justify-content-center gap-2">
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCurrentFileView(doc);
                          }}
                          title="View"
                        >
                          <FaEye size={12} />
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement("a");
                            link.href = doc.src;
                            link.download = doc.name;
                            link.click();
                          }}
                          title="Download"
                        >
                          <FaDownload size={12} />
                        </Button>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        )}

        {/* Document Preview Modal */}
        <Modal show={showModel} onHide={handleCloseFileView} size="lg" centered>
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              {currentDoc && getFileIcon(currentDoc.type)}
              <span className="fs-6">{currentDoc?.name}</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0 bg-light">
            {currentDoc?.type === "application/pdf" ? (
              <iframe
                title="PDF Viewer"
                src={currentDoc?.src}
                width="100%"
                height="550px"
                style={{ border: 'none' }}
              />
            ) : (
              <div className="text-center p-3">
                <img
                  className="img-fluid rounded"
                  src={currentDoc?.src}
                  alt={currentDoc?.name}
                  style={{ maxHeight: '550px', objectFit: 'contain' }}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" size="sm" onClick={handleDownload}>
              <FaDownload className="me-2" />
              Download
            </Button>
            <Button variant="secondary" size="sm" onClick={handleCloseFileView}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </div>
  );
};

export default CompanyView;